import { defineStore } from 'pinia'
import axios from 'axios'
import { authStore } from '@/stores/auth'

// Issue 622: CCP-side PDF estimate upload. CCP does not house ADAS inspections; these call the CCP API
// proxy (estimate/*), which resolves the owning parent firm's ADAS Map environment and forwards to
// bms/pdf/parse + bms/pdf/confirm. The created inspection returns to CCP via the opportunity writeback.
export const estimateStore = defineStore('estimate', {
  state: () => ({
    parsing: false,
    submitting: false,
    // True while a parsed PDF preview is on screen; the side-nav layout hides the menu for it.
    previewOpen: false
  }),
  actions: {
    setPreviewOpen(value) {
      this.previewOpen = !!value
    },
    // Returns PdfParseResult: { blobKey, fileName, contentType, byteSize, estimate }
    async parsePdf({ file, source, quality }) {
      const apiUrl = window.config ? window.config.apiUrl : ''
      const form = new FormData()
      form.append('file', file)
      if (source) form.append('source', source)
      if (quality) form.append('quality', quality)
      this.parsing = true
      try {
        const resp = await axios.post(`${apiUrl}/estimate/parse`, form, {
          headers: { Authorization: `Bearer ${authStore().token}` }
        })
        return resp.data
      } finally {
        this.parsing = false
      }
    },
    // Issue 622: where this upload lands (ADAS Map shop) + that environment's insurer list.
    // firm is honoured for super admins only; everyone else resolves from their own token.
    async getTarget(firm) {
      const apiUrl = window.config ? window.config.apiUrl : ''
      const q = firm ? `?firm=${encodeURIComponent(firm)}` : ''
      const resp = await axios.get(`${apiUrl}/estimate/target${q}`, {
        headers: { Authorization: `Bearer ${authStore().token}` }
      })
      return resp.data || {}
    },
    // Create an inspection from a VIN alone. Returns ADAS Map's own answer: { adas, adasInspection },
    // { vehicleChoices } when the VIN is ambiguous, or { error }.
    async createVinInspection(request) {
      const apiUrl = window.config ? window.config.apiUrl : ''
      const resp = await axios.post(`${apiUrl}/estimate/vinInspection`, request, {
        headers: { Authorization: `Bearer ${authStore().token}` }
      })
      return resp.data || {}
    },
    // Is this RO already used in the target ADAS Map shop? { exists, checked }
    async checkRoNumber(roNumber) {
      const apiUrl = window.config ? window.config.apiUrl : ''
      const resp = await axios.get(`${apiUrl}/estimate/roCheck?roNumber=${encodeURIComponent(roNumber)}`, {
        headers: { Authorization: `Bearer ${authStore().token}` }
      })
      return resp.data || {}
    },
    // Upload Estimate Review: list the firm's uploaded estimate PDFs (proxied to ADAS Map).
    // firm is honoured for super admins only; everyone else is scoped to their own by the API.
    async getDocuments(firm) {
      const apiUrl = window.config ? window.config.apiUrl : ''
      const q = firm ? `?firm=${encodeURIComponent(firm)}` : ''
      const resp = await axios.get(`${apiUrl}/estimate/documents${q}`, {
        headers: { Authorization: `Bearer ${authStore().token}` }
      })
      return resp.data || []
    },
    // Returns PdfEstimateConfirmResult: { adas, adasInspection, scrubbed }
    async confirmPdf(payload) {
      const apiUrl = window.config ? window.config.apiUrl : ''
      this.submitting = true
      try {
        const resp = await axios.post(`${apiUrl}/estimate/confirm`, payload, {
          headers: { Authorization: `Bearer ${authStore().token}`, 'Content-Type': 'application/json' }
        })
        return resp.data
      } finally {
        this.submitting = false
      }
    }
  }
})
